import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader2, Users, UserPlus, Mail, Chrome, HelpCircle, RefreshCw, Calendar, Clock,
  Download, Search,
} from 'lucide-react';
import { getSignupStats, getSignupIps } from '../services/supabase';
import { resolveIPs, isPublicIP, type GeoIPResult } from '../services/geoip';
import { acquisitionSourceLabel, isGoogleAdsAttribution } from '../lib/attribution';
import type { SignupMetrics, SignupRow } from '../types';
import SignupWorldMap, { countryFlag } from './SignupWorldMap';

type Period = { label: string; days: number };

const PERIODS: Period[] = [
  { label: '24 hours', days: 1 },
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: 'All time', days: 0 },
];

const CUSTOM_DAYS = -1;

const londonDate = (d = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);

const daysAgoLondon = (days: number) =>
  londonDate(new Date(Date.now() - days * 24 * 60 * 60 * 1000));

const APP_LABELS: Record<string, string> = {
  photoclear: 'Photoclear',
  staging: 'Virtual Staging',
  sketchplan: 'Sketchplan',
  showcase: 'Showcase',
  doorsteps: 'Doorsteps',
  tours: 'Tours',
  studio: 'ReHub Studio',
  sales: 'Sales',
  tools: 'Tools',
  hub: 'Hub',
  hdrphoto: 'HDR Photo',
  valmate: 'Valmate',
  photoplan: 'Photoplan',
  'photoplan-ai': 'Photoplan AI',
  photoedit: 'Photo Edit',
  videomotion: 'Videomotion',
  imagemotion: 'Imagemotion',
  hyperdusk: 'Hyperdusk',
  floorplan: 'Floorplan',
  mediahub: 'MediaHub',
  propertycut: 'PropertyCuts',
  'planos-studio': 'Planos Studio',
  content: 'Content',
  worlds: '360Worlds',
  flow: 'Flow',
  propertycgi: 'PropertyCGI',
  'vss-shop': 'VSS Shop',
  unknown: 'Unknown',
};

const appLabel = (slug: string) => APP_LABELS[slug] || slug;

const csvEscape = (value: string | number | boolean | null | undefined) => {
  const s = value == null ? '' : String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename: string, headers: string[], rows: Array<Array<string | number | boolean | null | undefined>>) => {
  const lines = [
    headers.map(csvEscape).join(','),
    ...rows.map((row) => row.map(csvEscape).join(',')),
  ];
  const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const fmtDateTime = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    timeZone: 'Europe/London',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const providerLabel = (provider: string) =>
  provider.toLowerCase() === 'email' ? 'Email link' : provider;

const sourceOf = (row: SignupRow) =>
  row.acquisition_source || acquisitionSourceLabel(row);

const sourceDetail = (row: SignupRow): Array<[string, string]> => {
  const items: Array<[string, string]> = [];
  if (row.utm_campaign) items.push(['Campaign', row.utm_campaign]);
  if (row.utm_content) items.push(['Ad group / content', row.utm_content]);
  if (row.utm_term) items.push(['Keyword / term', row.utm_term]);
  if (row.landing_page) items.push(['Landing page', row.landing_page]);
  items.push(['Signed up', fmtDateTime(row.created_at)]);
  return items;
};

const sourceTone = (label: string) => {
  if (label === 'Google Ads') return 'bg-amber-500/10 text-amber-200 border-amber-500/30';
  if (label === 'Unknown') return 'bg-slate-800 text-slate-500 border-slate-700';
  return 'bg-teal-500/10 text-teal-200 border-teal-500/20';
};

const providerBadge = (provider: string) => {
  const p = provider.toLowerCase();
  if (p === 'google') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-300 border border-blue-500/20">
        <Chrome size={12} /> Google
      </span>
    );
  }
  if (p === 'email') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-700/40 text-slate-300 border border-slate-600/40">
        <Mail size={12} /> Email link
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-300 border border-purple-500/20">
      {provider}
    </span>
  );
};

const chartTitle = (granularity?: SignupMetrics['granularity'], isHourly?: boolean) => {
  if (isHourly || granularity === 'hour') return 'Signups per hour';
  if (granularity === 'week') return 'Signups per week';
  return 'Signups per day';
};

const PIE_COLORS = [
  '#3b82f6', // blue
  '#f97316', // orange
  '#22c55e', // green
  '#a855f7', // purple
  '#eab308', // yellow
  '#ec4899', // pink
  '#14b8a6', // teal
  '#ef4444', // red
  '#06b6d4', // cyan
  '#f59e0b', // amber
  '#84cc16', // lime
  '#8b5cf6', // violet
];
const pieColor = (i: number) => PIE_COLORS[i % PIE_COLORS.length];

const DonutChart: React.FC<{
  segments: { label: string; value: number; color: string }[];
  total: number;
  size?: number;
}> = ({ segments, total, size = 132 }) => {
  const stroke = Math.round(size * 0.18);
  const radius = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#1e293b" strokeWidth={stroke} />
        {total > 0 &&
          segments.map((s) => {
            if (s.value <= 0) return null;
            const dash = (s.value / total) * circumference;
            const el = (
              <circle
                key={s.label}
                cx={cx}
                cy={cy}
                r={radius}
                fill="none"
                stroke={s.color}
                strokeWidth={stroke}
                strokeDasharray={`${dash} ${circumference - dash}`}
                strokeDashoffset={-offset}
              >
                <title>{`${s.label}: ${s.value} (${Math.round((s.value / total) * 100)}%)`}</title>
              </circle>
            );
            offset += dash;
            return el;
          })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <span className="text-xl font-bold text-white leading-none">{total}</span>
        <span className="text-[10px] text-slate-500 mt-0.5">signups</span>
      </div>
    </div>
  );
};

const AdminSignupsPanel: React.FC = () => {
  const [days, setDays] = useState<number>(30);
  const [customFrom, setCustomFrom] = useState<string>(daysAgoLondon(30));
  const [customTo, setCustomTo] = useState<string>(londonDate());
  const [appFilter, setAppFilter] = useState<string>('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [data, setData] = useState<SignupMetrics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [geoData, setGeoData] = useState<Map<string, GeoIPResult>>(new Map());
  const [geoLoading, setGeoLoading] = useState(false);
  const [countryFilter, setCountryFilter] = useState('');
  const [openSourceId, setOpenSourceId] = useState<string | null>(null);

  const isCustom = days === CUSTOM_DAYS;

  const load = async () => {
    if (isCustom && (!customFrom || !customTo)) return;
    setLoading(true);
    setError(null);
    setCountryFilter('');
    const result = await getSignupStats(isCustom ? CUSTOM_DAYS : days, 5000, {
      from: isCustom ? customFrom : null,
      to: isCustom ? customTo : null,
      app: appFilter || null,
      provider: providerFilter || null,
    });
    if (!result.success) {
      setError(result.error || 'Failed to load signups');
      setData(null);
      setLoading(false);
      return;
    }

    const ipRes = await getSignupIps(result.signups.map((s) => s.id));
    const merged: SignupMetrics = ipRes.success
      ? {
          ...result,
          signups: result.signups.map((s) => ({
            ...s,
            ip_address: ipRes.ips[s.id] || s.ip_address || null,
          })),
        }
      : result;
    setData(merged);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, customFrom, customTo, appFilter, providerFilter]);

  useEffect(() => {
    if (!data?.signups?.length) {
      setGeoData(new Map());
      setGeoLoading(false);
      return;
    }
    const ips = data.signups.map((s) => s.ip_address).filter(isPublicIP);
    if (ips.length === 0) {
      setGeoData(new Map());
      setGeoLoading(false);
      return;
    }
    let cancelled = false;
    setGeoLoading(true);
    resolveIPs(ips, (partial) => {
      if (!cancelled) setGeoData(partial);
    }).then((final) => {
      if (!cancelled) {
        setGeoData(final);
        setGeoLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [data]);

  const maxDay = useMemo(() => {
    if (!data?.per_day?.length) return 0;
    return Math.max(...data.per_day.map((d) => d.total), 1);
  }, [data]);

  const providerEntries = useMemo(
    () => Object.entries(data?.by_provider || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );
  const appEntries = useMemo(
    () => Object.entries(data?.by_app || {}).sort((a, b) => b[1] - a[1]),
    [data]
  );

  const appOptions = useMemo(() => {
    const slugs = new Set<string>([...Object.keys(APP_LABELS), ...Object.keys(data?.by_app || {})]);
    return Array.from(slugs)
      .filter((s) => s !== 'unknown' || (data?.by_app || {})[s])
      .sort((a, b) => appLabel(a).localeCompare(appLabel(b)));
  }, [data]);

  const windowTotal = data?.stats.total ?? 0;
  const googleAdsCount = typeof data?.stats.google_ads === 'number'
    ? data.stats.google_ads
    : (data?.signups || []).filter((s) => isGoogleAdsAttribution(s)).length;
  const isHourly = days === 1 || data?.granularity === 'hour';
  const last24h = data?.stats.last_24h ?? (days === 1 ? windowTotal : 0);
  const attributed = useMemo(() => {
    if (!data) return 0;
    return Object.entries(data.by_app).reduce(
      (sum, [app, n]) => (app === 'unknown' ? sum : sum + n),
      0
    );
  }, [data]);

  const sourceOptions = useMemo(() => {
    const present = new Set((data?.signups || []).map((s) => sourceOf(s)));
    const rest = Array.from(present)
      .filter((label) => label !== 'Google Ads' && label !== 'Unknown')
      .sort((a, b) => a.localeCompare(b));
    return ['Google Ads', ...rest, 'Unknown'];
  }, [data]);

  const filteredSignups = useMemo(() => {
    let rows = data?.signups || [];
    if (countryFilter) {
      rows = rows.filter((s) => geoData.get(s.ip_address || '')?.country === countryFilter);
    }
    if (sourceFilter) {
      rows = rows.filter((s) => sourceOf(s) === sourceFilter);
    }
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((s) => {
      const geo = geoData.get(s.ip_address || '');
      const hay = [
        s.email, s.name, s.email_domain, s.company, s.signup_app,
        appLabel(s.signup_app || 'unknown'), s.ip_address, geo?.city, geo?.country,
        sourceOf(s), s.utm_campaign, s.utm_content, s.utm_term, s.landing_page,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [data, search, countryFilter, sourceFilter, geoData]);

  const periodSlug = () => {
    if (isCustom && customFrom && customTo) return `${customFrom}-to-${customTo}`;
    if (days === 0) return 'all-time';
    if (days === 1) return '24h';
    return `${days}d`;
  };

  const exportSignups = (rows: SignupRow[]) => {
    downloadCsv(
      `signups-detailed-${periodSlug()}.csv`,
      [
        'Signed up (London)',
        'Signed up (ISO)',
        'Email',
        'Name',
        'Domain',
        'Company',
        'Auth method',
        'Source',
        'Campaign',
        'Ad group / content',
        'Keyword / term',
        'Landing page',
        'App source',
        'Credits',
        'Last sign-in (ISO)',
        'Email confirmed',
        'Marketing consent',
        'Referral code',
        'Plan',
        'Has purchased',
        'Credits purchased',
        'IP',
        'City',
        'Country',
        'User ID',
      ],
      rows.map((s) => {
        const geo = geoData.get(s.ip_address || '');
        return [
          fmtDateTime(s.created_at),
          s.created_at,
          s.email,
          s.name,
          s.email_domain || '',
          s.company || '',
          providerLabel(s.provider),
          sourceOf(s),
          s.utm_campaign || '',
          s.utm_content || '',
          s.utm_term || '',
          s.landing_page || '',
          s.signup_app ? appLabel(s.signup_app) : 'Unknown',
          s.credits ?? '',
          s.last_sign_in_at || '',
          s.email_confirmed ? 'yes' : 'no',
          s.marketing_consent ? 'yes' : 'no',
          s.referred_by_code || '',
          s.subscription_plan || '',
          s.has_purchased ? 'yes' : 'no',
          s.credits_purchased ?? 0,
          s.ip_address || '',
          geo?.city || '',
          geo?.country || '',
          s.id,
        ];
      })
    );
  };

  const exportSummary = () => {
    if (!data) return;
    const dayRows = data.per_day.map((d) => [d.day, d.google, d.email, d.other, d.total]);
    const appRows = appEntries.map(([app, n]) => [
      appLabel(app),
      n,
      windowTotal ? `${Math.round((n / windowTotal) * 100)}%` : '0%',
    ]);
    const providerRows = providerEntries.map(([prov, n]) => [
      providerLabel(prov),
      n,
      windowTotal ? `${Math.round((n / windowTotal) * 100)}%` : '0%',
    ]);
    const lines = [
      ['Period', periodSlug()].map(csvEscape).join(','),
      ['Total sign-ups', windowTotal].map(csvEscape).join(','),
      ['Google Ads sign-ups', googleAdsCount].map(csvEscape).join(','),
      '',
      [isHourly || data.granularity === 'hour' ? 'Hour' : data.granularity === 'week' ? 'Week starting' : 'Day', 'Google', 'Email link', 'Other', 'Total'].map(csvEscape).join(','),
      ...dayRows.map((row) => row.map(csvEscape).join(',')),
      '',
      ['App source', 'Count', 'Share'].map(csvEscape).join(','),
      ...appRows.map((row) => row.map(csvEscape).join(',')),
      '',
      ['Auth method', 'Count', 'Share'].map(csvEscape).join(','),
      ...providerRows.map((row) => row.map(csvEscape).join(',')),
    ];
    const countryCounts = new Map<string, number>();
    data.signups.forEach((s) => {
      const country = geoData.get(s.ip_address || '')?.country;
      if (!country) return;
      countryCounts.set(country, (countryCounts.get(country) || 0) + 1);
    });
    const located = Array.from(countryCounts.values()).reduce((a, b) => a + b, 0);
    if (countryCounts.size) {
      lines.push(
        '',
        ['Country', 'Count', 'Share'].map(csvEscape).join(','),
        ...Array.from(countryCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([country, n]) =>
            [country, n, located ? `${Math.round((n / located) * 100)}%` : '0%'].map(csvEscape).join(',')
          ),
      );
    }
    const blob = new Blob(['\uFEFF' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `signups-summary-${periodSlug()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectPeriod = (next: number) => {
    setDays(next);
    if (next === CUSTOM_DAYS) {
      setCustomFrom(daysAgoLondon(30));
      setCustomTo(londonDate());
    }
  };

  const selectInputClass =
    'bg-black border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-orange-500';

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {PERIODS.map((p) => (
            <button
              key={p.days}
              onClick={() => selectPeriod(p.days)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                days === p.days
                  ? 'bg-orange-600 text-white'
                  : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => selectPeriod(CUSTOM_DAYS)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isCustom
                ? 'bg-orange-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            Custom
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => filteredSignups.length && exportSignups(filteredSignups)}
            disabled={!data || filteredSignups.length === 0}
            className="px-3 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-200 hover:text-white hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none inline-flex items-center gap-2"
            title="Download one row per signup"
          >
            <Download size={16} /> Signups CSV
          </button>
          <button
            onClick={exportSummary}
            disabled={!data}
            className="px-3 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-200 hover:text-white hover:bg-slate-700 disabled:opacity-40 disabled:pointer-events-none inline-flex items-center gap-2"
            title="Download daily totals, app and auth breakdowns"
          >
            <Download size={16} /> Summary CSV
          </button>
          <button
            onClick={load}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {isCustom && (
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-400">From:</label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className={selectInputClass}
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-slate-400">To:</label>
            <input
              type="date"
              value={customTo}
              max={londonDate()}
              onChange={(e) => setCustomTo(e.target.value)}
              className={selectInputClass}
            />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email, name, city, country, source…"
            className={`${selectInputClass} w-full pl-9`}
          />
        </div>
        <select
          value={appFilter}
          onChange={(e) => setAppFilter(e.target.value)}
          className={selectInputClass}
        >
          <option value="">All apps</option>
          {appOptions.map((slug) => (
            <option key={slug} value={slug}>{appLabel(slug)}</option>
          ))}
        </select>
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className={selectInputClass}
        >
          <option value="">All auth methods</option>
          <option value="google">Google</option>
          <option value="email">Email link</option>
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className={selectInputClass}
          aria-label="Source"
        >
          <option value="">All sources</option>
          {sourceOptions.map((label) => (
            <option key={label} value={label}>{label}</option>
          ))}
        </select>
        {(appFilter || providerFilter || sourceFilter || search || countryFilter) && (
          <button
            onClick={() => {
              setAppFilter('');
              setProviderFilter('');
              setSourceFilter('');
              setSearch('');
              setCountryFilter('');
            }}
            className="text-sm text-slate-400 hover:text-white"
          >
            Clear filters
          </button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="animate-spin text-orange-500" size={32} />
        </div>
      )}

      {!loading && error && (
        <div className="bg-red-950/40 border border-red-800 rounded-2xl p-6 text-red-300 text-sm">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total sign-ups</p>
                  <p className="text-2xl md:text-3xl font-bold text-white mt-1">{windowTotal}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {isCustom ? 'Selected range' : days === 0 ? 'All time' : days === 1 ? 'Last 24 hours' : `Last ${days} days`}
                  </p>
                </div>
                <UserPlus size={30} className="text-orange-400" />
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Google Ads</p>
                  <p className="text-2xl md:text-3xl font-bold text-white mt-1">{googleAdsCount}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {isCustom ? 'Selected range' : days === 0 ? 'All time' : days === 1 ? 'Last 24 hours' : `Last ${days} days`}
                  </p>
                </div>
                <UserPlus size={30} className="text-amber-300" />
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">{days === 1 ? 'Today' : 'Last 24 hours'}</p>
                  <p className="text-2xl md:text-3xl font-bold text-white mt-1">
                    {days === 1 ? data.stats.today : last24h}
                  </p>
                </div>
                {days === 1 ? (
                  <Calendar size={30} className="text-slate-500" />
                ) : (
                  <Clock size={30} className="text-slate-500" />
                )}
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Last 7 days</p>
                  <p className="text-2xl md:text-3xl font-bold text-white mt-1">{data.stats.last_7d}</p>
                </div>
                <Users size={30} className="text-slate-500" />
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">All time</p>
                  <p className="text-2xl md:text-3xl font-bold text-white mt-1">{data.stats.all_time}</p>
                </div>
                <TrendUp />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 space-y-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white">{chartTitle(data.granularity, isHourly)}</h3>
                <div className="flex items-center gap-3 text-xs text-slate-400">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Google
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm bg-orange-500" /> Email link
                  </span>
                </div>
              </div>
              {data.per_day.length === 0 ? (
                <p className="text-slate-500 text-sm py-8 text-center">No signups in this period.</p>
              ) : (
                <div className={`flex items-end h-48 overflow-x-auto ${isHourly ? 'gap-0.5' : 'gap-1.5'}`}>
                  {data.per_day.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-2 group min-w-[14px]">
                      <div className="w-full flex flex-col justify-end items-center h-40 relative">
                        <span className="absolute -top-0 text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          {d.total}
                        </span>
                        <div
                          className={`w-full bg-orange-500 rounded-t-sm ${isHourly ? '' : 'max-w-[26px]'}`}
                          style={{ height: `${(d.email / maxDay) * 100}%` }}
                          title={`${d.day}: ${d.email} email`}
                        />
                        <div
                          className={`w-full bg-blue-500 rounded-b-sm ${isHourly ? '' : 'max-w-[26px]'}`}
                          style={{ height: `${((d.google + d.other) / maxDay) * 100}%` }}
                          title={`${d.day}: ${d.google + d.other} google/other`}
                        />
                      </div>
                      <span className="text-[9px] text-slate-500 whitespace-nowrap rotate-0 truncate w-full text-center">
                        {isHourly ? d.day.replace(/:00$/, '') : d.day.replace(/^\w+ /, '')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-white">By app source</h3>
                <span className="text-[11px] text-slate-500">{attributed}/{windowTotal} tagged</span>
              </div>
              {appEntries.length === 0 ? (
                <p className="text-slate-500 text-sm py-6 text-center">No signups in this period.</p>
              ) : (
                <div className="flex flex-col sm:flex-row items-center gap-6">
                  <DonutChart
                    size={160}
                    total={appEntries.reduce((sum, [, n]) => sum + n, 0)}
                    segments={appEntries.map(([app, n], i) => ({
                      label: appLabel(app),
                      value: n,
                      color: app === 'unknown' ? '#475569' : pieColor(i),
                    }))}
                  />
                  <div className="w-full flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 max-h-52 overflow-y-auto pr-1">
                    {appEntries.map(([app, n], i) => (
                      <button
                        key={app}
                        onClick={() => setAppFilter(appFilter === app ? '' : app)}
                        className={`w-full flex items-center justify-between text-sm rounded-lg px-1 py-0.5 -mx-1 ${
                          appFilter === app ? 'bg-slate-800' : 'hover:bg-slate-800/50'
                        }`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-2.5 h-2.5 rounded-sm shrink-0"
                            style={{ backgroundColor: app === 'unknown' ? '#475569' : pieColor(i) }}
                          />
                          <span className={`truncate ${app === 'unknown' ? 'text-slate-500' : 'text-slate-300'}`}>
                            {appLabel(app)}
                          </span>
                        </span>
                        <span className="text-white font-medium shrink-0">
                          {n}
                          <span className="text-slate-500 ml-1.5 text-xs">
                            {windowTotal ? Math.round((n / windowTotal) * 100) : 0}%
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                <h3 className="text-sm font-semibold text-white mb-3">By auth method</h3>
                <div className="space-y-2">
                  {providerEntries.map(([prov, n]) => (
                    <button
                      key={prov}
                      onClick={() => setProviderFilter(providerFilter === prov ? '' : prov)}
                      className={`w-full flex items-center justify-between text-sm rounded-lg px-1 py-0.5 -mx-1 ${
                        providerFilter === prov ? 'bg-slate-800' : 'hover:bg-slate-800/50'
                      }`}
                    >
                      <span className="text-slate-300 capitalize">{providerLabel(prov)}</span>
                      <span className="text-white font-medium">
                        {n}
                        <span className="text-slate-500 ml-1.5 text-xs">
                          {windowTotal ? Math.round((n / windowTotal) * 100) : 0}%
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <SignupWorldMap
            points={(data.signups || [])
              .filter((s) => isPublicIP(s.ip_address))
              .map((s) => ({ ip: s.ip_address as string, email: s.email }))}
            geoData={geoData}
            loading={geoLoading}
            locatedCount={(data.signups || []).filter((s) => geoData.has(s.ip_address || '')).length}
            totalCount={data.signups.length}
            selectedCountry={countryFilter}
            onSelectCountry={setCountryFilter}
          />

          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-sm font-semibold text-white">
                Signups{' '}
                <span className="text-slate-500 font-normal">
                  ({filteredSignups.length}{(search || sourceFilter || countryFilter) ? ` of ${data.signups.length}` : ''}
                  {data.truncated ? `, first ${data.signups.length} of ${windowTotal}` : ''})
                </span>
              </h3>
              <span className="text-[11px] text-slate-500">newest first · times in London</span>
            </div>
            {data.truncated && (
              <div className="px-6 py-2 text-xs text-amber-300 bg-amber-950/30 border-b border-amber-900/40">
                Showing the {data.signups.length} most recent rows in this window. Narrow the date range or filters to export every signup.
              </div>
            )}
            <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-900 z-10">
                  <tr className="text-left text-slate-400 border-b border-slate-800">
                    <th className="px-6 py-3 font-medium">When</th>
                    <th className="px-6 py-3 font-medium">Email</th>
                    <th className="px-6 py-3 font-medium">Name</th>
                    <th className="px-6 py-3 font-medium">Location</th>
                    <th className="px-6 py-3 font-medium">IP</th>
                    <th className="px-6 py-3 font-medium">Method</th>
                    <th className="px-6 py-3 font-medium">Source</th>
                    <th className="px-6 py-3 font-medium">App source</th>
                    <th className="px-6 py-3 font-medium text-right">Credits</th>
                    <th className="px-6 py-3 font-medium">Purchased</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSignups.map((s) => {
                    const geo = geoData.get(s.ip_address || '');
                    const source = sourceOf(s);
                    const detail = sourceDetail(s);
                    const detailText = detail.map(([label, value]) => `${label}: ${value}`).join('\n');
                    return (
                    <React.Fragment key={s.id}>
                    <tr className="border-b border-slate-800/60 hover:bg-slate-800/30">
                      <td className="px-6 py-3 text-slate-300 whitespace-nowrap">{fmtDateTime(s.created_at)}</td>
                      <td className="px-6 py-3 text-white">{s.email}</td>
                      <td className="px-6 py-3 text-slate-400">{s.name || '—'}</td>
                      <td className="px-6 py-3 text-slate-300 whitespace-nowrap">
                        {geo ? (
                          <button
                            onClick={() => setCountryFilter(countryFilter === geo.country ? '' : geo.country)}
                            className="hover:text-white"
                            title={s.ip_address || ''}
                          >
                            <span className="mr-1">{countryFlag(geo.countryCode)}</span>
                            {geo.city}{geo.city && geo.country ? ', ' : ''}{geo.country}
                          </button>
                        ) : s.ip_address ? (
                          <span className="text-slate-600">{geoLoading ? 'Locating…' : '—'}</span>
                        ) : (
                          <span className="text-slate-600">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-slate-500 font-mono text-xs whitespace-nowrap">{s.ip_address || '—'}</td>
                      <td className="px-6 py-3">{providerBadge(s.provider)}</td>
                      <td className="px-6 py-3">
                        <button
                          type="button"
                          onClick={() => setOpenSourceId(openSourceId === s.id ? null : s.id)}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border hover:brightness-125 ${sourceTone(source)}`}
                          title={detailText}
                          aria-expanded={openSourceId === s.id}
                        >
                          {source}
                        </button>
                      </td>
                      <td className="px-6 py-3">
                        {s.signup_app ? (
                          <button
                            onClick={() => setAppFilter(s.signup_app)}
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-500/10 text-orange-300 border border-orange-500/20 hover:bg-orange-500/20"
                          >
                            {appLabel(s.signup_app)}
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                            <HelpCircle size={12} /> unknown
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right text-slate-300 tabular-nums">{s.credits ?? '—'}</td>
                      <td className="px-6 py-3">
                        {s.has_purchased ? (
                          <span className="text-xs font-medium text-emerald-300">
                            Yes{s.credits_purchased ? ` · ${s.credits_purchased}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-600">No</span>
                        )}
                      </td>
                    </tr>
                    {openSourceId === s.id && (
                      <tr className="border-b border-slate-800/60 bg-slate-950/70">
                        <td colSpan={10} className="px-6 py-3">
                          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-2 text-xs">
                            {detail.map(([label, value]) => (
                              <div key={label}>
                                <dt className="text-slate-500">{label}</dt>
                                <dd className="text-slate-200 break-all">{value}</dd>
                              </div>
                            ))}
                          </dl>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                  {filteredSignups.length === 0 && (
                    <tr>
                      <td colSpan={10} className="px-6 py-10 text-center text-slate-500">
                        No signups match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const TrendUp: React.FC = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-slate-500">
    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
    <polyline points="16 7 22 7 22 13" />
  </svg>
);

export default AdminSignupsPanel;
